import { AppliedFilters, Container, Table } from "@fluidattacks/design";
import type { Row } from "@tanstack/react-table";
import _ from "lodash";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { ActionButtons } from "./action-buttons";
import { useVulnsAssignedQuery } from "./hooks";
import {
  filteredContinuousVulnerabilitiesOnReattackIds,
  getColumns,
  isRejectedOrSafeOrSubmitted,
  isValidVulnerability,
  isVerificationRequestedOrOnHold,
} from "./utils";

import type { IOrganizationGroups } from "@types";
import { CVSS3Toggle } from "components/cvss3-toggle";
import { usePermissions } from "context/authz/hooks/use-permissions";
import { useAssignedVulnerabilitiesFilter } from "features/assigned-vulnerabilities-filter";
import { DeleteVulnerabilitiesButton } from "features/to-do/action-buttons/delete-vulnerabilities-button";
import { EditButton } from "features/to-do/action-buttons/edit-button";
import { RemoveVulnerabilityModal } from "features/vulnerabilities/remove-vulnerability-modal";
import { TreatmentModal } from "features/vulnerabilities/treatment-modal";
import type { IVulnRowAttr } from "features/vulnerabilities/types";
import { UpdateVerificationModal } from "features/vulnerabilities/update-verification-modal";
import {
  filterOutVulnerabilities,
  getNonSelectableVulnerabilitiesOnDeleteIds,
  onRemoveVulnerabilityResultHelper,
} from "features/vulnerabilities/utils";
import type { RemoveVulnerabilityMutationMutation } from "gql/graphql";
import { useDebouncedCallback } from "hooks";
import { useSettingsStore } from "hooks/use-settings-store";
import { useTable } from "hooks/use-table";
import { useVulnerabilityStore } from "hooks/use-vulnerability-store";
import { isSafe } from "pages/finding/vulnerabilities/utils";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";

const DEBOUNCE_DELAY_MS = 2000;

const AssignedVulnerabilities: FC = (): JSX.Element => {
  const { t } = useTranslation();
  const { cvssVersion } = useSettingsStore();
  const { isReattacking, resetVulnsStore, setIsReattacking } =
    useVulnerabilityStore();

  const [search, setSearch] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [iscurrentOpen, setIscurrentOpen] = useState<boolean[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [filteredData, setFilteredData] = useState<IVulnRowAttr[]>([]);
  const { organizations } = usePermissions();

  const { refetchVulnerabilitiesAssigned, vulnerabilities } =
    useVulnsAssignedQuery();

  const groups = useMemo(
    (): IOrganizationGroups["groups"] =>
      organizations.flatMap(
        (organization): IOrganizationGroups["groups"] =>
          organization.groups as IOrganizationGroups["groups"],
      ),
    [organizations],
  );

  const enabledRows = useCallback(
    (row: Row<IVulnRowAttr>): boolean => {
      if (isReattacking && isRejectedOrSafeOrSubmitted(row)) return false;
      if (isReattacking && isVerificationRequestedOrOnHold(row)) return false;
      if (isDeleting && isSafe(row)) return false;
      if (isReattacking && !isValidVulnerability(row.original, groups))
        return false;

      return true;
    },
    [isDeleting, isReattacking, groups],
  );

  const formattedVulns = useMemo(
    (): IVulnRowAttr[] =>
      vulnerabilities.filter(
        ({ groupName, organizationName, where }): boolean =>
          where.toLowerCase().includes(search.toLowerCase()) ||
          groupName.toLowerCase().includes(search.toLowerCase()) ||
          (organizationName?.toLowerCase().includes(search.toLowerCase()) ??
            false),
      ),
    [vulnerabilities, search],
  );

  const { Filters, options, removeFilter } = useAssignedVulnerabilitiesFilter({
    setFilteredData,
    vulnerabilities: formattedVulns,
  });

  const { table } = useTable({
    columns: getColumns({
      accessorKey: cvssVersion.scoreKey,
      header: cvssVersion.header,
    }),
    data: filteredData,
    id: "assigned-vulnerabilities-table",
    options: {
      actionsOptions: { onSearch: setSearch, search },
      filtersMode: "client-side",
      paginationMode: "client-side",
      rowOptions: { enableMultiSelection: true, validator: enabledRows },
    },
    total: filteredData.length,
  });
  const { rowSelection } = table.getState();
  const { data: tableData } = table.options.meta ?? {};
  const selectedVulns: IVulnRowAttr[] = Object.keys(rowSelection).map(
    (key): IVulnRowAttr => tableData?.get(key) ?? ({} as IVulnRowAttr),
  );

  const clearSelected = useCallback((): void => {
    table.setRowSelection({});
  }, [table]);

  const refreshAssigned = useCallback((): void => {
    refetchVulnerabilitiesAssigned().catch((): void => {
      Logger.error("An error occurred assigning vulnerabilities");
    });
  }, [refetchVulnerabilitiesAssigned]);

  const closeDeleteModal = useCallback((): void => {
    setIsDeleting(false);
    setIsDeleteOpen(false);
  }, []);

  const toggleEdit = useCallback((): void => {
    setIscurrentOpen(
      Object.entries(
        _.groupBy(selectedVulns, (vuln): string => vuln.groupName),
      ).map((__, index): boolean => index === 0),
    );
    setIsEditing(!isEditing);
  }, [isEditing, selectedVulns]);

  const handleCloseUpdateModal = useCallback(
    (index: number): void => {
      setIscurrentOpen((current): boolean[] => {
        const newCurrent = current.map(
          (isCurrentOpen, currentIndex): boolean => {
            if (currentIndex === index || currentIndex === index + 1) {
              return !isCurrentOpen;
            }

            return isCurrentOpen;
          },
        );
        if (newCurrent.every((isCurrentOpen): boolean => !isCurrentOpen)) {
          setIsEditing(false);
          clearSelected();
        }

        return newCurrent;
      });
    },
    [clearSelected],
  );

  const onReattack = useCallback((): void => {
    const validVulnerabilitiesId =
      filteredContinuousVulnerabilitiesOnReattackIds(selectedVulns, groups);
    const newValidVulnerabilities = Array.from(
      new Set(
        selectedVulns.filter((selectedVulnerability): boolean =>
          validVulnerabilitiesId.includes(selectedVulnerability.id),
        ),
      ),
    );
    if (selectedVulns.length > newValidVulnerabilities.length) {
      setIsReattacking(!isReattacking);
      if (newValidVulnerabilities.length === 0) {
        msgError(t("searchFindings.tabVuln.errors.selectedVulnerabilities"));
      } else {
        setIsOpen(true);
      }
    } else if (selectedVulns.length > 0) {
      setIsOpen(true);
      setIsReattacking(!isReattacking);
    } else {
      setIsReattacking(!isReattacking);
    }
  }, [groups, isReattacking, selectedVulns, setIsReattacking, t]);

  const onDelete = useCallback((): void => {
    if (isDeleting) {
      setIsDeleting(!isDeleting);
    } else {
      const newVulnerabilities = filterOutVulnerabilities(
        selectedVulns,
        vulnerabilities,
        getNonSelectableVulnerabilitiesOnDeleteIds,
      );
      if (selectedVulns.length > newVulnerabilities.length) {
        setIsDeleting(!isDeleting);
        if (newVulnerabilities.length === 0) {
          msgError(t("searchFindings.tabVuln.errors.selectedVulnerabilities"));
        } else {
          setIsDeleteOpen(true);
        }
      } else if (selectedVulns.length > 0) {
        setIsDeleteOpen(true);
        setIsDeleting(!isDeleting);
      } else {
        setIsDeleting(!isDeleting);
      }
    }
  }, [isDeleting, selectedVulns, t, vulnerabilities]);

  const refreshAssignedDebounced = useDebouncedCallback((): void => {
    refreshAssigned();
  }, DEBOUNCE_DELAY_MS);

  const onDeleteResult = useCallback(
    (removeVulnerabilityResult: RemoveVulnerabilityMutationMutation): void => {
      refreshAssignedDebounced();
      onRemoveVulnerabilityResultHelper(removeVulnerabilityResult, t);
      clearSelected();
      setIsDeleting(false);
      setIsDeleteOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearSelected],
  );

  const onClose = useCallback(
    (index: number): (() => void) =>
      (): void => {
        handleCloseUpdateModal(index);
      },
    [handleCloseUpdateModal],
  );

  const onCloseVerify = useCallback((): void => {
    setIsOpen(false);
    clearSelected();
  }, [clearSelected]);

  useEffect((): VoidFunction => {
    return (): void => {
      resetVulnsStore();
    };
  }, [resetVulnsStore]);

  return (
    <Fragment>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Filters />
            <Container gap={0.5} id={"actions-container"}>
              <CVSS3Toggle />
              <ActionButtons
                areVulnerabilitiesReattacked={
                  vulnerabilities.filter(
                    (vuln): boolean =>
                      !vuln.remediated &&
                      filteredContinuousVulnerabilitiesOnReattackIds(
                        vulnerabilities,
                        groups,
                      ).includes(vuln.id),
                  ).length === 0
                }
                areVulnsSelected={selectedVulns.length > 0}
                isRequestingReattack={isReattacking}
                onRequestReattack={onReattack}
                refreshAssigned={refreshAssigned}
              />
              <DeleteVulnerabilitiesButton
                areDeletableLocations={
                  vulnerabilities.filter(
                    (vuln): boolean => vuln.state !== "SAFE",
                  ).length > 0
                }
                areVulnsSelected={selectedVulns.length > 0}
                isDeleting={isDeleting}
                isEditing={isEditing}
                isRequestingReattack={isReattacking}
                onCancel={closeDeleteModal}
                onDeleting={onDelete}
              />
              <EditButton
                isDisabled={selectedVulns.length === 0}
                onEdit={toggleEdit}
              />
            </Container>
          </Table.Controls>
        </Table.Options>
        <Table.Caption>
          <AppliedFilters onClose={removeFilter} options={options} />
        </Table.Caption>
        <Table.Content />
        <Table.Footer />
      </Table>
      <RemoveVulnerabilityModal
        onClose={closeDeleteModal}
        onRemoveVulnRes={onDeleteResult}
        open={isDeleteOpen}
        vulnerabilities={selectedVulns}
      />
      {isOpen ? (
        <UpdateVerificationModal
          clearSelected={clearSelected}
          handleCloseModal={onCloseVerify}
          refetchData={refetchVulnerabilitiesAssigned}
          setRequestState={onReattack}
          setVerifyState={onReattack}
          vulns={selectedVulns}
        />
      ) : undefined}
      {isEditing && selectedVulns.length > 0
        ? Object.entries(
            _.groupBy(selectedVulns, (vuln): string => vuln.groupName),
          ).map(
            ([vulnGroupName, vulnerabilitiesToUpdated], index): JSX.Element => (
              <TreatmentModal
                findingId={""}
                groupName={vulnGroupName}
                handleClearSelected={clearSelected}
                key={vulnGroupName}
                modalProps={{
                  close: onClose(index),
                  isOpen: iscurrentOpen[index],
                  name: "edit-vulnerability-modal",
                  open: toggleEdit,
                  setIsOpen: setIsEditing,
                  toggle: open,
                }}
                refetchData={refetchVulnerabilitiesAssigned}
                vulnerabilities={vulnerabilitiesToUpdated}
              />
            ),
          )
        : undefined}
    </Fragment>
  );
};

export { AssignedVulnerabilities };
